import { useCallback, useEffect, useRef, useState } from "react";
import {
  MessageType,
  UseMessageHandlerProps,
  getMessageTimestamp,
} from "../interfaces/all";
import { useToast } from "./useToast";

const getMessageKey = (message: MessageType): string => {
  return `${message._id || message.timestamp}-${message.from}-${message.to}`;
};

const compressMessages = (messages: MessageType[]): MessageType[] => {
  // Primeiro, garante que todos os timestamps sejam objetos Date
  const normalizedMessages = messages.map((msg) => ({
    ...msg,
    timestamp: getMessageTimestamp(msg),
  }));

  // Remove duplicatas mantendo a mensagem mais recente
  const uniqueMessages = new Map<string, MessageType>();
  normalizedMessages.forEach((msg) => {
    const key = getMessageKey(msg);
    const existingMsg = uniqueMessages.get(key);

    if (
      !existingMsg ||
      msg.timestamp.getTime() > existingMsg.timestamp.getTime()
    ) {
      uniqueMessages.set(key, msg);
    }
  });

  // Ordena as mensagens por timestamp (mais antigas primeiro)
  return Array.from(uniqueMessages.values()).sort((a, b) => {
    // Compara os timestamps
    const timeA = a.timestamp.getTime();
    const timeB = b.timestamp.getTime();

    // Se os timestamps forem iguais, mantém a ordem original
    if (timeA === timeB) {
      return 0;
    }

    return timeA - timeB;
  });
};

export const useMessageHandler = ({
  socket,
  selectedConversation,
  onNewMessage,
  onMessagesUpdate,
}: UseMessageHandlerProps & {
  onMessagesUpdate?: (messages: MessageType[]) => void;
}) => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const { error: showErrorToast } = useToast();
  const isProcessingRef = useRef(false);
  const currentPhoneRef = useRef<string | null>(null);
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const MAX_PROCESSED_MESSAGES = 1000;

  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]); // Descomentado para limpar mensagens quando não há conversa
      currentPhoneRef.current = null;
      processedMessagesRef.current.clear();
      return;
    }

    // SEMPRE priorizar valid_telephone sobre telephone
    const phoneNumber =
      selectedConversation.valid_telephone || selectedConversation.telephone;

    if (currentPhoneRef.current !== phoneNumber) {
      currentPhoneRef.current = phoneNumber;
      processedMessagesRef.current.clear();
      setMessages([]); // Descomentado para limpar mensagens ao mudar de conversa
    }
  }, [selectedConversation]);

  const handleNewMessage = useCallback(
    (message: MessageType) => {
      if (!selectedConversation) {
        console.log(
          "📬 [SOCKET] Mensagem ignorada: Nenhuma conversa selecionada",
        );
        return;
      }

      // SEMPRE priorizar valid_telephone sobre telephone
      const phoneNumber =
        selectedConversation.valid_telephone || selectedConversation.telephone;

      // Normalizar números para comparação (mesma do backend)
      const normalizePhone = (phone: string) =>
        phone?.replace(/[^0-9]/g, "").replace(/^55/, "") || "";
      const normalizedPhoneNumber = normalizePhone(phoneNumber);
      const normalizedMessageFrom = normalizePhone(message.from || "");
      const normalizedMessageTo = normalizePhone(message.to || "");

      // Aceitar mensagens onde o número da conversa bate com from OU to
      const isRelevant =
        (normalizedMessageFrom === normalizedPhoneNumber ||
          normalizedMessageTo === normalizedPhoneNumber) &&
        currentPhoneRef.current === phoneNumber;

      console.log("📬 [SOCKET] Nova mensagem recebida:", {
        messageId: message._id,
        type: message.type,
        from: message.from,
        to: message.to,
        isRelevant,
        conversationPhone: phoneNumber,
        normalizedConv: normalizedPhoneNumber,
        normalizedFrom: normalizedMessageFrom,
        normalizedTo: normalizedMessageTo,
      });

      if (!isRelevant) {
        return;
      }

      const messageTimestamp = getMessageTimestamp(message);
      if (!messageTimestamp || isNaN(messageTimestamp.getTime())) {
        return;
      }

      // Gerar ID temporário se não existir
      const messageWithId = {
        ...message,
        _id:
          message._id ||
          `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        // Preservar o whatsappId se fornecido no evento
        rawWebhookData:
          message.rawWebhookData ||
          ((message as any).whatsappId
            ? { id: (message as any).whatsappId }
            : undefined),
        // GARANTIR STATUS INICIAL APENAS PARA MENSAGENS QUE ENVIAMOS (USER/ATTENDANT)
        statuses:
          message.statuses ||
          (message.sentBy === "user"
            ? [
                {
                  status: "sent",
                  timestamp: new Date(),
                },
              ]
            : []),
      } as MessageType;

      const messageKey = getMessageKey(messageWithId);
      if (processedMessagesRef.current.has(messageKey)) {
        return;
      }

      setMessages((prev) => {
        // Adiciona a nova mensagem ao array existente
        const newMessages = [
          ...prev,
          {
            ...messageWithId,
            timestamp: messageTimestamp,
          },
        ];

        // Ordena e comprime as mensagens
        const sortedMessages = compressMessages(newMessages);

        // Atualiza o cache de mensagens processadas com limite
        sortedMessages.forEach((msg) => {
          const key = getMessageKey(msg);
          processedMessagesRef.current.add(key);

          // Limitar tamanho do Set para evitar vazamento de memória
          if (processedMessagesRef.current.size > MAX_PROCESSED_MESSAGES) {
            const firstKey = processedMessagesRef.current.values().next().value;
            if (firstKey) {
              processedMessagesRef.current.delete(firstKey);
            }
          }
        });

        // Chamar callback se fornecido
        if (onMessagesUpdate) {
          onMessagesUpdate(sortedMessages);
        }

        return sortedMessages;
      });

      onNewMessage?.(messageWithId);
    },
    [selectedConversation, onNewMessage, onMessagesUpdate],
  );

  // Registrar listener para newMessage para processar mensagens da conversa selecionada
  useEffect(() => {
    const socketInstance = socket.current;

    if (!socketInstance) {
      return;
    }

    const handleMessage = (data: MessageType) => {
      if (isProcessingRef.current) {
        return;
      }
      isProcessingRef.current = true;

      try {
        handleNewMessage(data);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Erro ao processar mensagem";
        console.error("Erro ao processar mensagem:", error);
        showErrorToast(errorMessage);
      } finally {
        isProcessingRef.current = false;
      }
    };

    const handleMessageBatch = (
      data: MessageType[] | Record<string, MessageType>,
    ) => {
      if (isProcessingRef.current) {
        return;
      }
      isProcessingRef.current = true;

      try {
        // Converter para array se for objeto com índices numéricos
        const messagesArray = Array.isArray(data) ? data : Object.values(data);

        // Processar cada mensagem do batch
        messagesArray.forEach((message) => {
          handleNewMessage(message);
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Erro ao processar batch de mensagens";
        console.error("Erro ao processar batch:", error);
        showErrorToast(errorMessage);
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Registrar listener para newMessage
    socketInstance.on("newMessage", handleMessage);

    // Registrar listener para newMessageBatch
    socketInstance.on("newMessageBatch", handleMessageBatch);

    return () => {
      socketInstance.off("newMessage", handleMessage);
      socketInstance.off("newMessageBatch", handleMessageBatch);
    };
  }, [socket.current?.connected, handleNewMessage, showErrorToast, socket]);

  const setMessagesCallback = useCallback(
    (updater: MessageType[] | ((prev: MessageType[]) => MessageType[])) => {
      setMessages((prev) => {
        const newMessages =
          typeof updater === "function" ? updater(prev) : updater;

        if (selectedConversation) {
          const sortedMessages = compressMessages(newMessages);

          sortedMessages.forEach((msg) => {
            const key = getMessageKey(msg);
            processedMessagesRef.current.add(key);

            // Limitar tamanho do Set para evitar vazamento de memória
            if (processedMessagesRef.current.size > MAX_PROCESSED_MESSAGES) {
              const firstKey = processedMessagesRef.current
                .values()
                .next().value;
              if (firstKey) {
                processedMessagesRef.current.delete(firstKey);
              }
            }
          });

          // Chamar callback se fornecido
          onMessagesUpdate?.(sortedMessages);

          return sortedMessages;
        }

        // Chamar callback se fornecido
        onMessagesUpdate?.(newMessages);

        return newMessages;
      });
    },
    [selectedConversation, onMessagesUpdate, setMessages],
  );

  return {
    messages,
    setMessages: setMessagesCallback,
  };
};
